import { supabase } from '@/integrations/supabase/client';
import type {
  ComparisonData,
  CreateSharedLinkRequest,
  CreateSharedLinkResponse,
  ValidationError,
  SharedLink,
} from '@/types/sharedLinks';
import type { DiffsPagesEvent } from '@/components/reports/graphs/types';
import type { ExternalEventAnnotations } from '@/types/externalDiffs';

const SHARE_BASE_URL = 'https://pricingsaas.com/share';

/**
 * Generate a random 7-character alphanumeric short ID (client-side fallback)
 */
export function generateShortId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 7; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Build full share URL from short_id
 */
export function buildShareUrl(shortId: string): string {
  return `${SHARE_BASE_URL}/${shortId}`;
}

/**
 * Validate comparison data structure
 */
export function validateComparisonData(data: ComparisonData): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!data.url1 || typeof data.url1 !== 'string') {
    errors.push({ field: 'url1', message: 'url1 is required and must be a string' });
  }

  if (!data.url2 || typeof data.url2 !== 'string') {
    errors.push({ field: 'url2', message: 'url2 is required and must be a string' });
  }

  if (!data.w || typeof data.w !== 'number' || data.w <= 0) {
    errors.push({ field: 'w', message: 'w must be a positive number' });
  }

  if (!data.h || typeof data.h !== 'number' || data.h <= 0) {
    errors.push({ field: 'h', message: 'h must be a positive number' });
  }

  // Validate markers if present
  if (data.markers) {
    if (!Array.isArray(data.markers)) {
      errors.push({ field: 'markers', message: 'markers must be an array' });
    } else {
      data.markers.forEach((marker, index) => {
        if (typeof marker.x1 !== 'number' || typeof marker.y1 !== 'number' ||
            typeof marker.x2 !== 'number' || typeof marker.y2 !== 'number') {
          errors.push({
            field: `markers[${index}]`,
            message: 'marker coordinates (x1, y1, x2, y2) must be numbers'
          });
        }
        if (!marker.label || typeof marker.label !== 'string') {
          errors.push({
            field: `markers[${index}]`,
            message: 'marker label is required and must be a string'
          });
        }
      });
    }
  }

  return errors;
}

/**
 * Create a new shared link
 */
export async function createSharedLink(
  request: CreateSharedLinkRequest
): Promise<CreateSharedLinkResponse | { error: string }> {
  try {
    // 1. Validate comparison data
    const errors = validateComparisonData(request.comparison_data);
    if (errors.length > 0) {
      return { error: errors.map(e => `${e.field}: ${e.message}`).join(', ') };
    }

    // 2. Generate unique short_id via database function
    const { data: shortId, error: shortIdError } = await supabase.rpc('generate_short_id');

    if (shortIdError) {
      console.error('[createSharedLink] Failed to generate short_id:', shortIdError);
      return { error: 'Failed to generate unique identifier' };
    }

    if (!shortId) {
      console.error('[createSharedLink] No short_id returned from database');
      return { error: 'Failed to generate unique identifier' };
    }

    // 3. Insert shared link
    const { data, error } = await supabase
      .from('app_shared_links')
      .insert({
        short_id: shortId,
        comparison_data: request.comparison_data,
        title: request.title,
        link_type: 'before_and_after',
        campaign_source: request.campaign_source,
        campaign_medium: request.campaign_medium,
        campaign_name: request.campaign_name,
        campaign_content: request.campaign_content,
        expires_at: request.expires_at,
      })
      .select('id, short_id')
      .single();

    if (error) {
      console.error('[createSharedLink] Insert error:', error);
      return { error: error.message };
    }

    if (!data) {
      console.error('[createSharedLink] No data returned from insert');
      return { error: 'Failed to create shared link' };
    }

    // 4. Return short_id and full URL
    return {
      id: data.id,
      short_id: data.short_id,
      url: buildShareUrl(data.short_id),
    };
  } catch (err) {
    console.error('[createSharedLink] Unexpected error:', err);
    return { error: 'An unexpected error occurred' };
  }
}

/**
 * Get a shared link by short_id
 */
export async function getSharedLink(
  shortId: string
): Promise<SharedLink | { error: string }> {
  try {
    const { data, error } = await supabase
      .from('app_shared_links')
      .select('*')
      .eq('short_id', shortId)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('[getSharedLink] Query error:', error);
      return { error: error.message };
    }

    if (!data) {
      return { error: 'Link not found' };
    }

    // Check if expired
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return { error: 'Link has expired' };
    }

    return data as SharedLink;
  } catch (err) {
    console.error('[getSharedLink] Unexpected error:', err);
    return { error: 'An unexpected error occurred' };
  }
}

/**
 * Update campaign fields for a shared link (when report/table is renamed)
 */
export async function updateSharedLinkCampaign(
  linkId: string,
  campaign_name?: string,
  campaign_medium?: string
): Promise<{ success: boolean } | { error: string }> {
  try {
    const { error } = await supabase
      .from('app_shared_links')
      .update({
        campaign_name,
        campaign_medium,
      })
      .eq('id', linkId);

    if (error) {
      console.error('[updateSharedLinkCampaign] Update error:', error);
      return { error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('[updateSharedLinkCampaign] Unexpected error:', err);
    return { error: 'An unexpected error occurred' };
  }
}

/**
 * Extract original URLs from compare_viewer_url
 */
function extractOriginalUrlsFromCompareViewer(compareViewerUrl?: string): { url1?: string; url2?: string } {
  if (!compareViewerUrl) return {};

  try {
    const url = new URL(compareViewerUrl);
    const url1 = url.searchParams.get('url1');
    const url2 = url.searchParams.get('url2');

    return {
      url1: url1 ? decodeURIComponent(url1) : undefined,
      url2: url2 ? decodeURIComponent(url2) : undefined,
    };
  } catch (e) {
    console.error('[extractOriginalUrlsFromCompareViewer] Failed to parse URL:', e);
    return {};
  }
}

/**
 * Extract markers array from compare_viewer_url
 * This preserves all marker properties including hideMarker1/hideMarker2
 */
function extractMarkersFromCompareViewer(compareViewerUrl?: string): any[] | null {
  if (!compareViewerUrl) return null;

  try {
    const url = new URL(compareViewerUrl);
    const markersParam = url.searchParams.get('markers');

    if (!markersParam) return null;

    const markers = JSON.parse(decodeURIComponent(markersParam));
    return Array.isArray(markers) ? markers : null;
  } catch (e) {
    console.error('[extractMarkersFromCompareViewer] Failed to parse markers:', e);
    return null;
  }
}

/**
 * Extract caption from compare_viewer_url
 * Preserves the original caption if available
 */
function extractCaptionFromCompareViewer(compareViewerUrl?: string): string | null {
  if (!compareViewerUrl) return null;

  try {
    const url = new URL(compareViewerUrl);
    const caption = url.searchParams.get('caption');

    return caption ? decodeURIComponent(caption) : null;
  } catch (e) {
    console.error('[extractCaptionFromCompareViewer] Failed to parse caption:', e);
    return null;
  }
}

/**
 * Transform DiffsPagesEvent to ComparisonData
 * Extracts URLs and marker positions from event annotation
 */
export function buildComparisonDataFromEvent(
  event: DiffsPagesEvent,
  annotation?: ExternalEventAnnotations
): ComparisonData | null {
  // Priority order for getting ORIGINAL (unannotated) URLs:
  // 1. Extract from compare_viewer_url (best - has original URLs)
  // 2. Try cloudinary_urls if available
  // 3. Fall back to viewer URLs (but these might be annotated)

  const extractedUrls = extractOriginalUrlsFromCompareViewer(annotation?.compare_viewer_url);
  const url1 = extractedUrls.url1 || annotation?.cloudinary_urls?.primary_original_url || annotation?.before_viewer_url;
  const url2 = extractedUrls.url2 || annotation?.cloudinary_urls?.secondary_original_url || annotation?.after_viewer_url;

  if (!url1 || !url2) {
    console.warn('[buildComparisonDataFromEvent] Missing required URLs', {
      has_compare_viewer: !!annotation?.compare_viewer_url,
      extracted_urls: !!extractedUrls.url1 && !!extractedUrls.url2,
      has_cloudinary: !!annotation?.cloudinary_urls,
      has_before_viewer: !!annotation?.before_viewer_url,
      has_after_viewer: !!annotation?.after_viewer_url,
    });
    return null;
  }

  // Extract caption from compare_viewer_url (preserves original description)
  // Fallback to event.description if not in URL
  const extractedCaption = extractCaptionFromCompareViewer(annotation?.compare_viewer_url);

  // Build comparison data
  const comparisonData: ComparisonData = {
    url1,
    url2,
    w: 800,  // Default dimensions
    h: 600,
    label1: 'Before',
    label2: 'After',
    caption: extractedCaption || event.description,
  };

  // Add markers - PRIMARY: Extract from compare_viewer_url to preserve all properties
  // FALLBACK: Build from before_marker/after_marker coordinates
  const extractedMarkers = extractMarkersFromCompareViewer(annotation?.compare_viewer_url);

  if (extractedMarkers && extractedMarkers.length > 0) {
    // Use markers from compare_viewer_url (preserves hideMarker1/hideMarker2 and other properties)
    // Set marker label to full caption (same as title) for better readability
    const markerLabel = extractedCaption || event.description;
    comparisonData.markers = extractedMarkers.map(marker => ({
      ...marker,
      label: marker.label || markerLabel,
    }));
  } else if (annotation?.before_marker && annotation?.after_marker) {
    // Fallback: Build markers from coordinates (legacy behavior)
    const markerLabel = extractedCaption || event.description;
    comparisonData.markers = [{
      x1: annotation.before_marker.x,
      y1: annotation.before_marker.y,
      x2: annotation.after_marker.x,
      y2: annotation.after_marker.y,
      label: markerLabel,
      hideMarker1: false,
      hideMarker2: false,
    }];
  }

  return comparisonData;
}
